/**
 * Automatic Token Renewal
 * Ported from: innovation-claude/poc/mfe-shared/src/auth_renewal.rs
 *
 * Background task that:
 * - Checks token expiration every 60 seconds
 * - Renews when within 5 minutes of expiry
 * - Retries on failure with backoff
 * - Clears auth after max failures
 */

import { AuthStateManager } from './authStateManager'
import type { TradovateAuth } from './types'
import type { Transport } from '@innolab-trader/tradovate-transport'
import { renewAccessToken } from '@innolab-trader/tradovate-trading-service/ops/renewAccessToken'

/**
 * Renewal configuration
 */
interface RenewalConfig {
    checkInterval: number      // Milliseconds between checks (default: 60000 = 60s)
    renewalBuffer: number      // Milliseconds before expiry to renew (default: 300000 = 5min)
    maxFailures: number        // Max consecutive failures before clearing auth (default: 10)
}

const DEFAULT_CONFIG: RenewalConfig = {
    checkInterval: 60000,      // Check every 60 seconds
    renewalBuffer: 300000,     // Renew 5 minutes before expiry
    maxFailures: 10            // Clear auth after 10 failures (10 minutes)
}

/**
 * Renewal function type
 * Caller provides the actual renewal implementation
 */
type RenewTokenFn = (currentAuth: TradovateAuth) => Promise<TradovateAuth | null>

/**
 * Start automatic token renewal background task
 * Ported from auth_renewal.rs:26-108
 *
 * @param renewFn - Function that performs token renewal (provided by caller)
 * @param config - Optional renewal configuration
 * @returns Cleanup function to stop renewal
 */
export function startTokenRenewal(
    renewFn: RenewTokenFn,
    config: Partial<RenewalConfig> = {},
    opts?: { navigate?: (to: string) => void }
): () => void {
    const finalConfig: RenewalConfig = { ...DEFAULT_CONFIG, ...config }

    let consecutiveFailures = 0
    let timerId: ReturnType<typeof setTimeout> | null = null

    /**
     * Compute next backoff delay with jitter (base = checkInterval)
     */
    const nextDelay = () => {
        const base = finalConfig.checkInterval
        const exp = Math.min(consecutiveFailures, 6) // cap at 2^6 ~ 64x
        const jitter = Math.random() * 0.2 + 0.9 // 0.9–1.1
        return Math.round(base * Math.pow(2, exp) * jitter)
    }

    const scheduleNext = () => {
        const delay = consecutiveFailures > 0 ? nextDelay() : finalConfig.checkInterval
        timerId = setTimeout(checkAndRenew, delay)
    }

    /**
     * Check if token needs renewal and renew if necessary
     */
    const checkAndRenew = async () => {
        const auth = await AuthStateManager.load()

        if (!auth) {
            consecutiveFailures = 0
            return
        }

        // Check if token expires soon
        const timeUntilExpiry = AuthStateManager.getTimeUntilExpiration(auth)

        if (timeUntilExpiry < finalConfig.renewalBuffer) {
            console.log('🔄 Token expiring soon, attempting renewal...')

            try {
                // Call provided renewal function
                const renewedAuth = await renewFn(auth)

                if (renewedAuth) {
                    // Save renewed token to sessionStorage
                    await AuthStateManager.save(renewedAuth)

                    console.log(`✅ Token renewed successfully (expires: ${renewedAuth.expirationTime})`)
                    consecutiveFailures = 0
                } else {
                    throw new Error('Renewal function returned null')
                }
            } catch (error) {
                consecutiveFailures++
                console.error(`❌ Token renewal failed (${consecutiveFailures}/${finalConfig.maxFailures}):`, error)

                // Clear auth after too many failures
                if (consecutiveFailures >= finalConfig.maxFailures) {
                    console.error('🧹 Too many renewal failures, clearing auth')
                    await AuthStateManager.clear()
                    consecutiveFailures = 0

                    // Redirect to login
                    if (opts?.navigate) opts.navigate('/welcome')
                    else if (typeof window !== 'undefined') window.location.href = '/welcome'
                }
            }
        } else if (consecutiveFailures > 0) {
            // Token is valid, reset failure count
            consecutiveFailures = 0
        }
        scheduleNext()
    }

    // Kick off the loop immediately
    checkAndRenew()

    // Return cleanup function
    return () => {
        if (timerId) { clearTimeout(timerId); timerId = null; console.log('⏹️ Token renewal stopped') }
    }
}

/**
 * Convenience helper: start token renewal using the platform renew endpoint.
 *
 * Uses /auth/renewAccessToken via the provided transport, and persists the
 * returned access token + expiration into AuthStateManager.
 *
 * This centralizes the renewal policy so MFEs don't have to re-implement it.
 */
export function startTokenRenewalWithTransport(
  transport: Transport,
  config: Partial<RenewalConfig> = {},
  opts?: { navigate?: (to: string) => void }
): () => void {
  return startTokenRenewal(
    async (auth) => {
      try {
        const res = await renewAccessToken(transport, {})
        if (res?.accessToken && res?.expirationTime) {
          return {
            ...auth,
            accessToken: res.accessToken,
            mdAccessToken: res.mdAccessToken || auth.mdAccessToken,
            expirationTime: res.expirationTime,
          }
        }
        return null
      } catch {
        return null
      }
    },
    config,
    opts
  )
}
